import { Button, Col, Row, Spin } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next/";
import InfiniteScroll from "react-infinite-scroller";
import { setTitlePage } from "../../utils/helper";
import ResponsiveImage from "../../components/ResponsiveImage";
import { eventImageService } from "../../services";
import {
  IEventImageModel,
  IEventImageDetailWithOverlay,
} from "../../models/interface";
import "./style.scss";
import EventModal from "./EventModal";
import { WIDTH_MOBILE } from "../../common/constant";

const MAX_IMAGE_DETAILS = 6;
const LIMIT_EVENT_IMAGE = 3;

const Events = () => {
  const { t } = useTranslation();
  const ref: any = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // API state
  const [eventImages, setEventImages] = useState<Record<
    number,
    IEventImageModel[]
  > | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const { innerWidth } = window;
  const isMobile = innerWidth <= WIDTH_MOBILE;

  useEffect(() => {
    setTitlePage(t("title_page_event"));
  }, [t]);

  // Call event_image API on first mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setApiLoading(true);
      try {
        await fetchEventImagesWithDetails(1, false);
      } catch (error) {
        console.error("Error fetching initial event images:", error);
      } finally {
        setApiLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Common function to fetch event images with details
  const fetchEventImagesWithDetails = async (
    page: number = 1,
    append: boolean = false
  ) => {
    try {
      const response = await eventImageService.getEventImages({
        page,
        limit: LIMIT_EVENT_IMAGE,
      });

      // Check if response exists and is already grouped by year
      if (response && response.data) {
        // Set or append to eventImages state
        if (append) {
          setEventImages((prev: Record<number, IEventImageModel[]> | null) => {
            if (!prev) return response.data;

            const merged = { ...prev };
            Object.keys(response.data).forEach((year) => {
              const yearNum = parseInt(year);
              if (!merged[yearNum]) {
                merged[yearNum] = [];
              }
              // Ensure response[yearNum] is an array before spreading
              const yearEvents = Array.isArray(response.data[yearNum])
                ? response.data[yearNum]
                : [];
              merged[yearNum] = [...merged[yearNum], ...yearEvents];
            });
            return merged;
          });
        } else {
          setEventImages(response.data);
        }

        // Calculate hasMore based on total events across all years
        const totalEvents = Object.values(response.data).reduce(
          (total, events) =>
            total + (Array.isArray(events) ? events.length : 0),
          0
        );
        const hasMore = totalEvents === LIMIT_EVENT_IMAGE; // If we got full page, there might be more

        return {
          data: response.data,
          hasMore,
        };
      } else {
        if (!append) {
          setEventImages(null);
        }
        return {
          data: [],
          hasMore: false,
        };
      }
    } catch (error) {
      console.error("Error fetching event images:", error);
      throw error;
    }
  };

  const loadMore = async () => {
    if (loading || !eventImages) return;

    setLoading(true);
    try {
      const nextPage = currentPage + 1;
      const result = await fetchEventImagesWithDetails(nextPage, true);

      setHasMore(result.hasMore);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading more events:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (eventId: string) => {
    if (!ref || !ref.current) return;

    ref.current.openModal(eventId);
  };

  const renderImages = (eventImage: IEventImageModel) => {
    const { details = [] } = eventImage;
    const imageGrid: IEventImageDetailWithOverlay[] = details.slice(
      0,
      MAX_IMAGE_DETAILS - 1
    );

    if (details.length >= MAX_IMAGE_DETAILS) {
      imageGrid.push({ ...details[MAX_IMAGE_DETAILS - 1], overlay: true });
    }

    return (
      <Row gutter={[isMobile ? 10 : 20, isMobile ? 10 : 20]}>
        {imageGrid.map((image: IEventImageDetailWithOverlay, index: number) => (
          <Col key={index} xs={12} md={12}>
            <div className="event-image-container">
              <ResponsiveImage
                mediaId={image.picture_id || ""}
                mobileMediaId={image.mobile_picture_id || ""}
                alt={image.caption || ""}
                style={{ width: "100%" }}
              />
              {image.overlay && (
                <div className="image-overlay">
                  <Button
                    onClick={() => openModal(eventImage._id)}
                    className="overlay-btn"
                    type="link">
                    10+
                  </Button>
                </div>
              )}
              {image.caption && (
                <div className="image-caption">{image.caption}</div>
              )}
            </div>
          </Col>
        ))}
      </Row>
    );
  };

  const displayEvents = () => {
    if (!eventImages) {
      return null;
    }

    // Sort years in descending order
    const sortedYears = Object.keys(eventImages || {})
      .map(Number)
      .sort((a, b) => b - a);

    return sortedYears.map((year) => {
      const yearEvents =
        eventImages[year]?.filter((item) => item.details.length > 0) || [];

      if (yearEvents.length === 0) return null;

      return (
        <div key={year} className="event-item">
          <div className="event-year">
            <div className="event-year-title">
              <span className="icon">★</span>
              <span className="text">Năm {year}</span>
            </div>
          </div>

          <div className="event-content">
            {yearEvents.map((item) => (
              <div key={item._id} className="event-location-group">
                <div className="event-location">
                  <span className="icon">★</span>
                  <span>{item.name}</span>
                </div>
                {renderImages(item)}
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="event-container">
      <Row justify="center" className="event-title">
        <span className="large-title">HÌNH ẢNH SỰ KIỆN</span>
      </Row>

      {apiLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}>
          <Spin size="large" />
        </div>
      ) : eventImages && Object.keys(eventImages).length > 0 ? (
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={!loading && hasMore}
          loader={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px 0",
              }}>
              <Spin size="small" />
            </div>
          }
          threshold={200}>
          <div className="event-tabs-container">{displayEvents()}</div>
        </InfiniteScroll>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            color: "#999",
          }}>
          Không có dữ liệu sự kiện
        </div>
      )}
      <EventModal ref={ref} />
    </div>
  );
};

export default Events;
